/**
 * ArchiMate export functions
 * Supports PNG and ArchiMate XML (Open Exchange Format)
 */

import type { ArchiMateDiagram, ArchiMateElement, ArchiMateRelationship } from './types';

/**
 * Export diagram as PNG
 * Captures the SVG canvas and converts to PNG
 */
export async function exportToPNG(svgElement: SVGSVGElement, filename: string): Promise<void> {
  // Get SVG bounds
  const bbox = svgElement.getBBox();
  const padding = 50;

  // Create a new SVG with proper viewBox
  const svgClone = svgElement.cloneNode(true) as SVGSVGElement;
  svgClone.setAttribute('viewBox', `${bbox.x - padding} ${bbox.y - padding} ${bbox.width + padding * 2} ${bbox.height + padding * 2}`);
  svgClone.setAttribute('width', `${bbox.width + padding * 2}`);
  svgClone.setAttribute('height', `${bbox.height + padding * 2}`);

  // Convert SVG to data URL
  const svgString = new XMLSerializer().serializeToString(svgClone);
  const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);

  // Create canvas and draw SVG
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const img = new Image();

  return new Promise((resolve, reject) => {
    img.onload = () => {
      canvas.width = bbox.width + padding * 2;
      canvas.height = bbox.height + padding * 2;

      // White background
      ctx!.fillStyle = '#0A1E2E'; // Navy background
      ctx!.fillRect(0, 0, canvas.width, canvas.height);

      // Draw SVG
      ctx!.drawImage(img, 0, 0);

      // Convert to PNG and download
      canvas.toBlob((blob) => {
        if (blob) {
          const link = document.createElement('a');
          link.download = `${filename}.png`;
          link.href = URL.createObjectURL(blob);
          link.click();
          URL.revokeObjectURL(url);
          resolve();
        } else {
          reject(new Error('Failed to create PNG blob'));
        }
      }, 'image/png');
    };

    img.onerror = reject;
    img.src = url;
  });
}

/**
 * Export diagram as ArchiMate XML (Open Exchange Format)
 * Compatible with Archi tool and other ArchiMate tools
 */
export function exportToArchiMateXML(diagram: ArchiMateDiagram): string {
  const xmlns = 'http://www.opengroup.org/xsd/archimate/3.0/';
  const xsi = 'http://www.w3.org/2001/XMLSchema-instance';

  let xml = `<?xml version="1.0" encoding="UTF-8"?>\n`;
  xml += `<model xmlns="${xmlns}" xmlns:xsi="${xsi}" identifier="${diagram.id}">\n`;
  xml += `  <name>${escapeXML(diagram.name)}</name>\n`;

  // Elements
  xml += `  <elements>\n`;
  diagram.elements.forEach((element) => {
    xml += `    <element identifier="${element.id}" xsi:type="${element.type}">\n`;
    xml += `      <name>${escapeXML(element.name)}</name>\n`;
    if (element.documentation) {
      xml += `      <documentation>${escapeXML(element.documentation)}</documentation>\n`;
    }
    // Properties
    if (element.properties && Object.keys(element.properties).length > 0) {
      xml += `      <properties>\n`;
      Object.entries(element.properties).forEach(([key, value]) => {
        xml += `        <property key="${escapeXML(key)}" value="${escapeXML(value)}"/>\n`;
      });
      xml += `      </properties>\n`;
    }
    xml += `    </element>\n`;
  });
  xml += `  </elements>\n`;

  // Relationships
  xml += `  <relationships>\n`;
  diagram.relationships.forEach((rel) => {
    xml += `    <relationship identifier="${rel.id}" source="${rel.sourceId}" target="${rel.targetId}" xsi:type="${rel.type}">\n`;
    if (rel.name) {
      xml += `      <name>${escapeXML(rel.name)}</name>\n`;
    }
    if (rel.documentation) {
      xml += `      <documentation>${escapeXML(rel.documentation)}</documentation>\n`;
    }
    if (rel.accessType) {
      xml += `      <accessType>${rel.accessType}</accessType>\n`;
    }
    if (rel.influenceStrength) {
      xml += `      <strength>${rel.influenceStrength}</strength>\n`;
    }
    xml += `    </relationship>\n`;
  });
  xml += `  </relationships>\n`;

  // Views (diagram layout)
  xml += `  <views>\n`;
  xml += `    <diagrams>\n`;
  xml += `      <view identifier="${diagram.id}-view" xsi:type="Diagram">\n`;
  xml += `        <name>${escapeXML(diagram.name)}</name>\n`;

  // Element positions
  diagram.elements.forEach((element) => {
    xml += `        <node identifier="${element.id}-node" elementRef="${element.id}" x="${Math.round(element.x)}" y="${Math.round(element.y)}" w="${element.width || 120}" h="${element.height || 60}"/>\n`;
  });

  // Relationship connections
  diagram.relationships.forEach((rel) => {
    xml += `        <connection identifier="${rel.id}-conn" relationshipRef="${rel.id}" source="${rel.sourceId}-node" target="${rel.targetId}-node"/>\n`;
  });

  xml += `      </view>\n`;
  xml += `    </diagrams>\n`;
  xml += `  </views>\n`;

  xml += `</model>`;

  return xml;
}

/**
 * Download ArchiMate XML file
 */
export function downloadXML(diagram: ArchiMateDiagram): void {
  const xml = exportToArchiMateXML(diagram);
  const blob = new Blob([xml], { type: 'application/xml' });
  const link = document.createElement('a');
  link.download = `${diagram.name.replace(/[^a-z0-9]/gi, '_')}.archimate`;
  link.href = URL.createObjectURL(blob);
  link.click();
  URL.revokeObjectURL(link.href);
}

// Helper: Escape XML special characters
function escapeXML(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}
